import { faArrowRightLong, faBook, faBookOpen, faGraduationCap, faLightbulb, faLinesLeaning, faLock, faPencilRuler, faPlugCircleCheck, faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useMemo, useState } from "react";
import { useCookies } from "react-cookie";
import { useNavigate } from "react-router-dom";
import Header from "../../Components/Header/Header";
import "./Categories.css";
import ZenLogoWhite from "../../imgs/ZenLogo_white.svg";

// One entry per module of the app. `access` is the key stored on the user
// (CreateStudent / EditUser write these), `doc` the index of the documentation
// file fetched for the reader overlay, and `group` decides both the route and
// the column the module sits in.
interface ModuleDef {
    id: string;                     // route segment + activeModule value
    group: "low" | "high";
    access: string;
    doc: number;
    icon: any;
    ro: string; en: string;
    roDesc: string; enDesc: string;
}

const MODULES: ModuleDef[] = [
    {
        id: "edit", group: "low", access: "edit", doc: 0, icon: faPencilRuler,
        ro: "Editare", en: "Edit",
        roDesc: "Creezi o schemă electrică de la zero și o printezi în A4 / A3.",
        enDesc: "Draw an electrical scheme from scratch and print it in A4 / A3.",
    },
    {
        id: "functional", group: "low", access: "functional", doc: 1, icon: faLightbulb,
        ro: "Funcționare", en: "Functional",
        roDesc: "Vezi cum funcționează scheme predefinite, pas cu pas.",
        enDesc: "See how predefined schemes work, step by step.",
    },
    {
        id: "debugging", group: "low", access: "debug", doc: 2, icon: faPlugCircleCheck,
        ro: "Depanare", en: "Debug",
        roDesc: "Cauți defectul cu aparatul de măsură virtual.",
        enDesc: "Track the fault down with the virtual measuring device.",
    },
    {
        id: "learning", group: "high", access: "learn", doc: 3, icon: faLinesLeaning,
        ro: "Învățare", en: "Learning",
        roDesc: "Dispecerul dictează manevrele, tu le confirmi și le execuți.",
        enDesc: "The dispatcher dictates the maneuvers; you read them back and perform them.",
    },
    {
        id: "training", group: "high", access: "train", doc: 4, icon: faBook,
        ro: "Antrenare", en: "Training",
        roDesc: "Execuți manevre pe simulatorul de stație, fără dictare.",
        enDesc: "Run maneuvers on the station simulator, undictated.",
    },
    {
        id: "testing", group: "high", access: "test", doc: 5, icon: faGraduationCap,
        ro: "Testare", en: "Testing",
        roDesc: "Ești punctat după corectitudinea manevrelor executate.",
        enDesc: "You are scored on how correctly the maneuvers were executed.",
    },
];

const DOC_NAMES = ["edit", "functional", "debugging", "learning", "training", "testing"];

const Categories = ({ data, socket } : { data: any, socket: any }) => {

    const [cookies] = useCookies();
    const navigate = useNavigate();
    const en = cookies?.language === "en";

    if (!data?.userData) navigate(-1);

    const accessModules = useMemo(() => data.userData?.modules || ["edit", "functional", "debug", "learn", "train", "test"], []);   // eslint-disable-line react-hooks/exhaustive-deps
    const canOpen = (m: ModuleDef) => !!accessModules?.includes(m.access);

    // Start on the first module the account may actually open.
    const [activeModule, setActiveModule] = useState<string>((MODULES.find(canOpen) || MODULES[0]).id);
    const active = MODULES.find((m) => m.id === activeModule) || MODULES[0];

    const [changeModule, setChangeModule] = useState<number>(-1);
    const [changeModuleText, setChangeModuleText] = useState<string>("");

    let currIdx : number = 0;

    useEffect(() => {
        if (changeModule === -1) return;

        let timer : any;

        const smoothText = (texts : Array<string>, idx : number, initChangeModule : number, oldIdx : number = 0) => {
            if (idx === texts.length) return;
            const letters = texts[idx].split("");
            for (let letterIdx = 0; letterIdx < letters.length; letterIdx++) {
                let letter = letters[letterIdx];
                timer = setTimeout(() => {
                    if (oldIdx !== currIdx) return;
                    setChangeModuleText(currText => currText += letter);
                    if (letterIdx === letters.length - 1) {
                        return smoothText(texts, idx + 1, initChangeModule, oldIdx);
                    }
                }, letterIdx * 10);
            }
        };

        fetch(`${process.env.PUBLIC_URL}/Documentation_Modules/${DOC_NAMES[changeModule] || "edit"}_${cookies?.language || "en"}.txt`)
            .then(req => req.text())
            .then(res => {
                const texts = res.split(/^/gm);
                setChangeModuleText("");
                smoothText(texts, 0, changeModule);
            }).catch(err => {});

        return () => {
            currIdx++;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [changeModule]);

    const routeOf = (m: ModuleDef) => `/${m.group === "low" ? "low-tension" : "high-tension"}/${m.id}`;
    const openModule = (m: ModuleDef) => { if (canOpen(m)) navigate(routeOf(m)); };

    const column = (group: "low" | "high") => (
        <section className="cat-group">
            <p className={`cat-kicker ${group}`}>
                <span className="cat-kicker-dot" />
                {group === "low"
                    ? (en ? "Low-tension modules" : "Module joasă tensiune")
                    : (en ? "High-tension modules" : "Module înaltă tensiune")}
            </p>

            {MODULES.filter((m) => m.group === group).map((m) => {
                const locked = !canOpen(m);
                return (
                    <div key={m.id} className={`cat-card${activeModule === m.id ? " active" : ""}${locked ? " locked" : ""}`}>
                        <button
                            type="button"
                            className="cat-card-main"
                            disabled={locked}
                            aria-pressed={activeModule === m.id}
                            onClick={() => setActiveModule(m.id)}
                            onDoubleClick={() => openModule(m)}
                        >
                            <span className="cat-card-icon"><FontAwesomeIcon icon={locked ? faLock : m.icon} /></span>
                            <span className="cat-card-name">{en ? m.en : m.ro}</span>
                        </button>

                        <button
                            type="button"
                            className="cat-card-docs"
                            title={en ? "Documentation" : "Documentație"}
                            aria-label={`${en ? "Documentation" : "Documentație"}: ${en ? m.en : m.ro}`}
                            onClick={() => setChangeModule(m.doc)}
                        >
                            <FontAwesomeIcon icon={faBookOpen} />
                        </button>
                    </div>
                );
            })}
        </section>
    );

    return (
        <div className="content categories-content cat-page">
            <Header data={data} socket={socket} />

            <div className="cat-main">
                <header className="cat-head">
                    <h2 className="title">{en ? "Modules" : "Module"}</h2>
                </header>

                <div className="block-modules">
                    <div className="modules-container">
                        {column("low")}

                        {/* Preview of the selected module: the photo behind a pane of
                            glass, with the name, the description and the way in. */}
                        <div
                            className={`cat-preview ${active.group === "low" ? "low-tension" : "high-tension"} ${active.id}`}
                            onClick={() => openModule(active)}
                        >
                            <img className="cat-preview-logo" src={ZenLogoWhite} alt="zen logo" />

                            <div className="cat-preview-caption">
                                <div className="cat-preview-text">
                                    <h2 className="cat-preview-title">{en ? active.en : active.ro}</h2>
                                    <p>{en ? active.enDesc : active.roDesc}</p>
                                </div>
                                <span className="cat-preview-cta">
                                    {en ? "Open" : "Deschide"} <FontAwesomeIcon icon={faArrowRightLong} />
                                </span>
                            </div>
                        </div>

                        {column("high")}
                    </div>
                </div>

                <div className="start-button-container-2">
                    <button
                        type="button"
                        className="start-button"
                        disabled={!canOpen(active)}
                        onClick={() => openModule(active)}
                    >
                        START <span>{en ? active.en : active.ro}</span>
                    </button>
                </div>
            </div>

            <div className={`docs-container ${changeModule !== -1 ? "active" : ""}`}>
                <textarea value={changeModuleText} disabled={true} />
                <FontAwesomeIcon icon={faTimes} className="close-icon" onClick={() => {
                    setChangeModule(-1);
                    setChangeModuleText("");
                }} />
            </div>

        </div>
    );
};

export default Categories;
